/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { AudioService } from './AudioService';
import type { EditorService } from './EditorService';
import type { UIService } from './UIService';
import type { DialogService } from './DialogService';

/**
 * Centralizes keyboard shortcut handling.
 */
export class KeyboardService {
  private statusManager: StatusManager;
  
  // Service registry getters
  private get audioService() { return ServiceRegistry.get<AudioService>('audioService'); }
  private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
  private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
  private get dialogService() { return ServiceRegistry.get<DialogService>('dialogService'); }

  constructor() {
    this.statusManager = new StatusManager(ServiceRegistry.get('dom'));
    this.bindEventListeners();
  }

  private bindEventListeners(): void {
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));
    window.addEventListener('keyup', (e) => this.handleKeyUp(e));
  }

  private async handleKeyDown(event: KeyboardEvent): Promise<void> {
    const activeEl = document.activeElement as HTMLElement;
    const isEditingField = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.isContentEditable
    );

    if (event.ctrlKey && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      this.uiService.toggleToolbarView('find');
      return;
    }

    // Undo/Redo shortcuts
    if (isEditingField && (activeEl.id === 'polishedNote' || activeEl.id === 'translatedNote')) {
        if (event.ctrlKey && event.key.toLowerCase() === 'z') {
            event.preventDefault();
            if (event.shiftKey) {
                this.editorService.redo(); // Ctrl+Shift+Z
            } else {
                this.editorService.undo(); // Ctrl+Z
            }
            return;
        }
        if (event.ctrlKey && event.key.toLowerCase() === 'y') {
            event.preventDefault();
            this.editorService.redo(); // Ctrl+Y
            return;
        }
    }
    
    // Prevent recording shortcuts if an editable field is focused
    if (isEditingField) return;
    
    if (event.ctrlKey && event.code === 'Space') {
      event.preventDefault();
      this.audioService.toggleRecording();
      return;
    }
    
    if (event.code !== 'Space' || event.repeat) return;
    event.preventDefault();
    if (!this.audioService.getIsRecording()) {
      this.audioService.setPushToTalkRecording(true);
      await this.audioService.startRecording(true);
    }
  }

  private async handleKeyUp(event: KeyboardEvent): Promise<void> {
    const activeEl = document.activeElement as HTMLElement;
    const isEditingField = activeEl && (
        activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.tagName === 'SELECT' ||
        activeEl.isContentEditable
    );
    if (event.code !== 'Space' || isEditingField) return;
    event.preventDefault();
    if (this.audioService.getIsRecording() && this.audioService.getIsPushToTalkRecording()) {
      await this.audioService.stopRecording();
    }
  }
}